import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Trash2, Loader2, FileText, Calendar, Sparkles } from 'lucide-react';
import SummaryDisplay    from '../components/documents/SummaryDisplay';
import KeywordsDisplay   from '../components/documents/KeywordsDisplay';
import DefinitionsDisplay from '../components/documents/DefinitionsDisplay';
import QuizDisplay       from '../components/documents/QuizDisplay';
import { useDocuments }  from '../hooks/useDocuments';
import { useAuth }       from '../context/AuthContext';
import { ROUTES }        from '../lib/constants';
import { supabase }      from '../lib/supabase';
import toast             from 'react-hot-toast';
import { formatDate }    from '../utils/formatters';
import { exportDocumentToPDF } from '../utils/pdfExport';

const DocumentView = () => {
  const { id }           = useParams();
  const navigate         = useNavigate();
  const queryClient      = useQueryClient();
  const { isPremium, user } = useAuth();
  const { deleteDocument } = useDocuments();

  const [deleting,       setDeleting]       = useState(false);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);

  // ── Fetch the document ────────────────────────────────────────────────────
  const {
    data: document,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['document', id],
    queryFn: async () => {
      const { data, error: fetchErr } = await supabase
        .from('documents')
        .select('*')
        .eq('id', id)
        .eq('user_id', user?.id)
        .single();

      if (fetchErr || !data) {
        toast.error('Document not found');
        navigate(ROUTES.HISTORY);
        return null;
      }
      return data;
    },
    enabled: !!id && !!user?.id,
    staleTime: 0,
    // Poll every 4s ONLY while still processing — stops automatically on completion
    refetchInterval: (query) =>
      query.state.data?.processing_status === 'processing' ? 4000 : false,
  });

  // ── FIX: Real-time subscription for this specific document ────────────────
  // Listens for updates on this document so the page flips to "completed"
  // instantly without waiting for the next poll cycle
  useEffect(() => {
    if (!id || !user?.id) return;

    const channel = supabase
      .channel(`document-view-${id}`)
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'documents',
          filter: `id=eq.${id}`,
        },
        (payload) => {
          console.log('DocumentView real-time update:', payload.new?.processing_status)

          // Patch the cache in-place — instant UI update, no loading flash
          queryClient.setQueryData(['document', id], (old) =>
            old ? { ...old, ...payload.new } : payload.new
          )

          // If it just completed, also refresh the history list and stats
          if (payload.new?.processing_status === 'completed') {
            queryClient.invalidateQueries({ queryKey: ['documents',      user.id] })
            queryClient.invalidateQueries({ queryKey: ['dashboardStats', user.id] })
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [id, user?.id, queryClient])

  if (error) {
    console.error('Error fetching document:', error)
    toast.error('Failed to load document')
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) return

    setDeleting(true)
    try {
      await deleteDocument(id)
      toast.success('Document deleted successfully')
      navigate(ROUTES.HISTORY)
    } catch (err) {
      console.error('Error deleting document:', err)
      toast.error('Failed to delete document')
      setDeleting(false)
    }
  }

  // ── PDF Export ────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    if (!isPremium) { toast.error('PDF export is a Premium feature'); return }
    exportDocumentToPDF(document)
    toast.success('PDF downloaded!')
  }

  // ── FIX: Quiz generation — was passing full URL instead of function name ──
  const handleGenerateQuiz = async () => {
    if (!isPremium) { toast.error('Quiz generation is a Premium feature'); return }

    setGeneratingQuiz(true)
    try {
      const { data: result, error: functionError } = await supabase.functions.invoke(
        'process-document',   // ← just the function name, not the full URL
        {
          body: {
            documentId:       document.id,
            imageUrl:         document.file_url,
            generateQuizOnly: true,
            userId:           user?.id,
          },
        }
      )

      if (functionError) throw new Error(functionError.message)

      if (result?.success) {
        toast.success('AI Quiz generated successfully!')
        queryClient.invalidateQueries({ queryKey: ['document', id] })
      }
    } catch (err) {
      toast.error(err.message || 'Error connecting to AI service')
    } finally {
      setGeneratingQuiz(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-brown-50">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">

        {/* Document Header */}
        <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-lg border border-cream-300/50 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-brown-500 to-brown-700 rounded-xl flex items-center justify-center shadow-md">
                    <FileText className="w-6 h-6 md:w-7 md:h-7 text-cream-50" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-3xl font-bold text-brown-900 mb-2 break-words min-h-[2rem]">
                    {loading
                      ? <div className="h-7 w-2/3 bg-brown-200/50 rounded animate-pulse" />
                      : document?.file_name
                    }
                  </h1>
                  <div className="flex flex-wrap items-center gap-3">
                    {loading ? (
                      <>
                        <div className="h-5 w-16 bg-brown-200/50 rounded-full animate-pulse" />
                        <div className="h-5 w-24 bg-brown-200/50 rounded-full animate-pulse" />
                      </>
                    ) : (
                      <>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          document?.processing_status === 'completed'  ? 'bg-green-100 text-green-700' :
                          document?.processing_status === 'processing' ? 'bg-blue-100  text-blue-700'  :
                                                                          'bg-red-100   text-red-700'
                        }`}>
                          {document?.processing_status}
                        </span>
                        <span className="flex items-center text-xs text-brown-600">
                          <Calendar className="w-4 h-4 mr-1.5" />
                          {formatDate(document?.created_at, true)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:w-auto">
              {!loading && isPremium && document?.processing_status === 'completed' && (
                <button
                  onClick={handleExportPDF}
                  className="flex items-center justify-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-brown-600 to-brown-700 text-white rounded-lg transition-all shadow-md font-medium"
                >
                  <Download className="w-5 h-5" />
                  <span>Export PDF</span>
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={loading || deleting}
                className="flex items-center justify-center space-x-2 px-5 py-2.5 border-2 border-red-200 text-red-600 hover:bg-red-50 rounded-lg transition-all font-medium disabled:opacity-50"
              >
                {deleting
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <Trash2  className="w-5 h-5" />
                }
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="backdrop-blur-sm bg-white/40 rounded-xl p-12 text-center border border-cream-300/50 space-y-4">
            <div className="h-6 w-1/4 bg-brown-200/50 rounded animate-pulse mx-auto" />
            <div className="h-4 w-3/4 bg-brown-100/50 rounded animate-pulse mx-auto" />
            <div className="h-4 w-1/2 bg-brown-100/50 rounded animate-pulse mx-auto" />
          </div>
        ) : !document ? (
          <div className="backdrop-blur-sm bg-white/40 rounded-xl p-12 text-center border border-cream-300/50">
            <FileText className="w-16 h-16 text-brown-300 mx-auto mb-4" />
            <p className="text-xl text-brown-700 font-medium">Document not found</p>
          </div>
        ) : (
          <>
            {document.processing_status === 'failed' && (
              <div className="backdrop-blur-sm bg-red-50/50 rounded-xl p-8 text-center border-2 border-dashed border-red-200 animate-fade-in">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-red-600 rotate-180" />
                </div>
                <h3 className="text-xl font-bold text-red-900 mb-2">Processing Failed</h3>
                <p className="text-red-700 mb-6 max-w-md mx-auto">
                  We encountered an error while trying to simplify this page. This usually happens with blurry images or network glitches.
                </p>
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['document', id] })}
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg transition-all font-bold"
                >
                  Retry Connection
                </button>
              </div>
            )}

            {document.processing_status === 'processing' && (
              <div className="backdrop-blur-sm bg-white/40 rounded-xl p-12 text-center border border-cream-300/50">
                <Loader2 className="w-12 h-12 text-brown-600 animate-spin mx-auto mb-4" />
                <h3 className="text-xl font-bold text-brown-900 mb-2">Simplifying your textbook...</h3>
                <p className="text-brown-600">Our AI is reading the page. This usually takes 10–20 seconds.</p>
              </div>
            )}

            {document.processing_status === 'completed' && (
              <div className="space-y-6">
                <SummaryDisplay
                  summary={document.summary}
                  extractedText={document.extracted_text}
                  examples={document.examples}
                  studyTips={document.study_tips}
                />
                <KeywordsDisplay   keywords={document.keywords}       />
                <DefinitionsDisplay definitions={document.definitions} />

                {document.quiz ? (
                  <QuizDisplay quiz={document.quiz} documentId={document.id} />
                ) : isPremium ? (
                <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 text-center border-2 border-dashed border-brown-200">
                  <Sparkles className="w-12 h-12 text-brown-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-brown-900 mb-2">Ready for a Challenge?</h3>
                  
                  {/* ── FIX: message changes based on whether file_url still exists ── */}
                  <p className="text-brown-600 mb-6">
                    {document.file_url
                      ? "This document doesn't have a quiz yet. Let our AI create one specifically for your exam prep!"
                      : "Quiz generation requires the original image. Please re-upload this document to generate a quiz."}
                  </p>

                  {/* ── FIX: button disabled if no file_url to send to Gemini ── */}
                  <button
                    onClick={handleGenerateQuiz}
                    disabled={generatingQuiz || !document.file_url}
                    className="px-8 py-3 bg-brown-600 hover:bg-brown-700 text-white rounded-xl shadow-lg transition-all flex items-center mx-auto space-x-2 disabled:opacity-70"
                  >
                    {generatingQuiz ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Creating your quiz...</span>
                      </>
                    ) : (
                      <span>Generate AI Quiz</span>
                    )}
                  </button>
                </div>
                ) : (
                  <QuizDisplay quiz={null} documentId={document.id} />
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default DocumentView;